import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	clearChatSession,
	closeChatInterface,
	loadMoreChatHistoryRequest,
	openChatInterfaceRequest,
	sendChatMessageRequest,
} from '@/features/ai-tutor/store/actions';
import {
	getChatHistoryLoading,
	getChatLastEvaluatedKey,
	getChatMessageObj,
	getChatMessages,
	getShowChat,
} from '@/features/ai-tutor/store/selectors';

import TutorChatContent from '@/features/ai-tutor/components/TutorChatContent';
import TutorChatHeader from '@/features/ai-tutor/components/TutorChatHeader';

import tutorQ from '@/assets/images/ai-tutor/tutor-q.png';

import 'katex/dist/katex.min.css';
import '@/features/ai-tutor/styles/ai-tutor.scss';

import type { ForwardedRef } from 'react';
import type {
	AiTutorChatContext,
	AiTutorQuestionData,
	TutorButton,
	TutorMessagePayload,
} from '@/features/ai-tutor/types';

export interface AiTutorChatHandle {
	open: (context: AiTutorChatContext) => void;
	close: () => void;
}

interface AiTutorChatProps {
	onFirstUserMessage: () => void;
	onMinimize: () => void;
	onOpenRequest: () => void | Promise<void>;
	onRefreshToken: () => Promise<boolean>;
}

const AI_TUTOR_API_URL = import.meta.env.VITE_AI_TUTOR_API_URL;

const AiTutorChat = (
	{ onFirstUserMessage, onMinimize, onOpenRequest, onRefreshToken }: AiTutorChatProps,
	ref: ForwardedRef<AiTutorChatHandle>,
) => {
	const [showLoadMore, setShowLoadMore] = useState(false);
	const [scrollToBottom, setScrollToBottom] = useState(true);
	const messages = useSelector(getChatMessages);
	const showChat = useSelector(getShowChat);
	const lastEvaluatedKey = useSelector(getChatLastEvaluatedKey);
	const isOldMsgLoading = useSelector(getChatHistoryLoading);
	const messageObj = useSelector(getChatMessageObj);
	const dispatch = useDispatch();

	const messagesEndRef = useRef<HTMLDivElement | null>(null);
	const historyContainerRef = useRef<HTMLUListElement | null>(null);

	const headerTitle = 'Superhulp Q';
	const noteAIMessage = 'Q is AI en kan fouten maken.';

	const openChatInterface = (
		questionData: AiTutorQuestionData,
		topicTitle: AiTutorChatContext['topicTitle'],
		questionInstructions: AiTutorChatContext['questionInstructions'],
		topicId: AiTutorChatContext['topicId'],
		exerciseId: AiTutorChatContext['exerciseId'],
	) => {
		dispatch(
			openChatInterfaceRequest({
				apiUrl: AI_TUTOR_API_URL,
				context: {
					questionData,
					topicTitle,
					questionInstructions,
					topicId,
					exerciseId,
				},
				onRefreshToken,
			}),
		);
	};

	const closeChatInterfaceFunc = () => {
		dispatch(clearChatSession());
	};

	useEffect(() => {
		const chatHistoryContainer = document.querySelector<HTMLElement>('.chat-history-container');
		const handleScroll = () => {
			if (chatHistoryContainer?.scrollTop === 0) {
				setShowLoadMore(true);
			}
		};

		if (chatHistoryContainer) {
			chatHistoryContainer.addEventListener('scroll', handleScroll);
		}

		return () => {
			if (chatHistoryContainer) {
				chatHistoryContainer.removeEventListener('scroll', handleScroll);
			}
		};
	}, [showChat]);

	const handleLoadMore = () => {
		setShowLoadMore(false);
		setScrollToBottom(false);
		dispatch(
			loadMoreChatHistoryRequest({
				apiUrl: AI_TUTOR_API_URL,
				onRefreshToken,
				callback: () => {
					window.requestAnimationFrame(() => {
						if (historyContainerRef.current) {
							historyContainerRef.current.scrollTop =
								historyContainerRef.current.scrollHeight / 2;
						}
					});
				},
			}),
		);
	};

	useEffect(() => {
		dispatch(clearChatSession());
	}, [dispatch]);

	useImperativeHandle(ref, () => ({
		open: ({ questionData, topicTitle, questionInstructions, topicId, exerciseId }) => {
			openChatInterface(questionData, topicTitle, questionInstructions, topicId, exerciseId);
		},
		close: closeChatInterfaceFunc,
	}));

	useEffect(() => {
		if (scrollToBottom && messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView();
			setScrollToBottom(true);
		}
	}, [messages, scrollToBottom]);

	useEffect(() => {
		const chatHistoryContainer = document.querySelector('.chat-history-container');
		if (chatHistoryContainer) {
			chatHistoryContainer.scrollTop = chatHistoryContainer.scrollHeight;
		}
	}, [showChat]);

	const handleSend = (onButtonClick = true, nextMessageObj: TutorMessagePayload = messageObj) => {
		setScrollToBottom(true);
		dispatch(
			sendChatMessageRequest({
				apiUrl: AI_TUTOR_API_URL,
				onButtonClick,
				messageObj: nextMessageObj,
				onFirstUserMessage,
				onRefreshToken,
			}),
		);
	};

	const minimiseChat = async () => {
		onMinimize();
		dispatch(closeChatInterface(false));
	};

	const messageButtonAction = (buttonObj: TutorButton): boolean => {
		if (buttonObj.buttonAction === 'HANDLE_ERROR_RESEND') {
			handleSend(false, messageObj);
			return true;
		}
		if (buttonObj.buttonAction === 'ANSWER_OPTIONS') {
			handleSend(true, {
				user_answer: buttonObj.buttonText,
				tutor_question: messageObj.tutor_question,
			});
			return true;
		}
		if (buttonObj.buttonAction === 'I_DONT_UNDERSTAND_OPTIONS') {
			handleSend(true, {
				user_answer: `question_doubt: ${buttonObj.buttonText}`,
				tutor_question: messageObj.tutor_question,
			});
			return true;
		}
		if (buttonObj.buttonAction === 'I_DONT_UNDERSTAND_ENABLER') {
			handleSend(true, {
				user_answer: `question_doubt_enabler: ${buttonObj.buttonText}`,
				tutor_question: messageObj.tutor_question,
			});
			return true;
		}

		return false;
	};

	return (
		<div className="ai-tutor">
			<button className="ai-tutor__fab" type="button" onClick={onOpenRequest}>
				<span className="ai-tutor__fab-label">Hulp nodig?</span>
				<img src={tutorQ} className="tutor-img" alt="tutor-q" />
			</button>
			{showChat && (
				<div className="ai-tutor__paper">
					<TutorChatHeader title={headerTitle} minimiseChat={minimiseChat} />
					<ul className="ai-tutor__list chat-history-container" ref={historyContainerRef}>
						{showLoadMore && lastEvaluatedKey && (
							<div style={{ display: 'flex', justifyContent: 'center' }}>
								<button
									className="ai-tutor__history-button"
									type="button"
									onClick={handleLoadMore}
									disabled={isOldMsgLoading}
								>
									{isOldMsgLoading ? (
										<span
											className="ai-tutor__spinner"
											style={{ marginRight: 5 }}
											aria-label="Meer laden"
										/>
									) : (
										<span
											className="ai-tutor__icon ai-tutor__icon--small icon-arrow-up"
											aria-hidden="true"
										/>
									)}{' '}
									<span className="history-text">Meer laden</span>
								</button>
							</div>
						)}
						<TutorChatContent
							messages={messages}
							messagesEndRef={messagesEndRef}
							onMessageButtonAction={messageButtonAction}
						/>
					</ul>
					<p className="ai-tutor__note">{noteAIMessage}</p>
				</div>
			)}
		</div>
	);
};

const ForwardedAiTutorChat = forwardRef(AiTutorChat);

ForwardedAiTutorChat.displayName = 'AiTutorChat';

export default ForwardedAiTutorChat;
